import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import Swal from 'sweetalert2';
import { NetworkContract } from "../../models/NetworkContract";
import { OperatorService } from "../../services/operator.service";
import { ModalDismissReasons, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { map } from "rxjs/internal/operators";
import { ConventionFilter } from "../../models/ConventionFilter";
import { PlaceConvention } from "../../models/PlaceConvention";
import { mergeMap } from 'rxjs/operators';
import { Page } from '../../../../shared/model/page';
import { ToastrService } from 'ngx-toastr';
import { PlaceFilterForm } from '../../models/PlaceFilterForm';
import { Place } from '../../../account/models/Place';
import { CountyDTO } from '../../../shared/models/CountyDTO';
import { CityDTO } from '../../../shared/models/CityDTO';
import { AddressService } from '../../../shared/services/address.service';

@Component({
  selector: 'app-electricity-consumption-place',
  templateUrl: './network-contract-electricity.component.html',
  styleUrls: ['./network-contract-electricity.component.scss']
})
export class NetworkContractElectricityComponent implements OnInit, OnDestroy {

  modalReference: NgbModalRef;

  public form: FormGroup;
  public conventionForm: FormGroup;
  public newNetworkContract = new NetworkContract();
  public conventionFilter = new ConventionFilter();
  public places = [];
  public networkContractId: number;
  public conventionParts: string[] = [];
  public placeForm: FormGroup;
  public consumptionPlacesListVisible: boolean = false;
  public isSupplierSelected: boolean;
  public isConventionUpdated: boolean = false;
  public electricityConsumptionPlaces: Page<Place>;
  public placeFilterForm: PlaceFilterForm = new PlaceFilterForm();
  public paginationElectricityConsumptionPlaces = [];
  public counties: CountyDTO[] = [];
  public cities: CityDTO[] = [];

  private formSubscriptions: Subscription[] = [];
  private routeDataSub: Subscription;
  closeResult: string = '';

  @Input()
  public editable = false;

  @Input()
  set place(networkContract: any) {
    this.form.patchValue(networkContract);
  }

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private operatorService: OperatorService,
    private addressService: AddressService,
    private modalService: NgbModal,
    private toastrService: ToastrService,
  ) {

  }

  createNetworkContractForm(): void {
    this.form = this.formBuilder.group({
      client: ['', Validators.required],
      clientType: ['', Validators.required],
      clientCode: ['', Validators.required],
      clientName: ['', Validators.required],
      contractNumber: ['', Validators.required],
      date: ['', Validators.required],
    });
  }

  createConventionForm(): void {
    this.conventionForm = this.formBuilder.group({
      conventionNumber: ['', Validators.required],
      conventionDate: ['', Validators.required],
    });
  }

  createPlaceFrom(): void {
    this.placeForm = this.formBuilder.group({
      client: [''],
      code: [''],
      county: [''],
      city: [''],
    });
    this.formSubscriptions.push(
      this.placeForm.get('client').valueChanges.subscribe(value => this.placeFilterForm.client = value)
    );
    this.formSubscriptions.push(
      this.placeForm.get('code').valueChanges.subscribe(value => this.placeFilterForm.code = value)
    );
    this.formSubscriptions.push(
      this.placeForm.get('county').valueChanges.subscribe(value => this.placeFilterForm.countyId = value)
    );
    this.formSubscriptions.push(
      this.placeForm.get('city').valueChanges.subscribe(value => this.placeFilterForm.cityId = value)
    );
  }

  ngOnInit(): void {
    this.createNetworkContractForm();
    this.createConventionForm();
    this.createPlaceFrom();

    this.addressService.getCounties().subscribe(data => this.counties = data);
    this.routeDataSub = this.route
      .paramMap
      .pipe(
        map(() => {
          if (window.history.state.editable) {
            this.editable = !!window.history.state.editable;
          }
          this.networkContractId = +window.history.state.networkContractId;
          return this.networkContractId;
        }),
        mergeMap(networkContractId => this.operatorService.getElectricityNetworkContract(networkContractId))
      ).subscribe((networkContract) => {
        this.form.patchValue(networkContract);
        this.places = networkContract.places;
        this.consumptionPlacesListVisible = true;
        this.searchPlaces();
      });
  }

  getFormData() {
    this.operatorService.getElectricityNetworkContract(this.networkContractId).subscribe(data => {
      this.form.patchValue(data);
      this.places = data.places;
    });
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.validationNotification('Vă rugăm să completați toate câmpurile marcate cu asterix')
    } else {
      this.newNetworkContract.clientCode = this.form.get('clientCode').value;
      this.newNetworkContract.clientName = this.form.get('clientName').value;
      this.newNetworkContract.contractNumber = this.form.get('contractNumber').value;
      this.newNetworkContract.date = this.form.get('date').value;
      this.newNetworkContract.client = this.form.get('client').value;
      this.newNetworkContract.clientType = this.form.get('clientType').value;
      Swal.fire({
        title: 'Esti sigur?',
        text: "Esti pe cale sa adaugi un nou contract de retea.",
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Da',
        cancelButtonText: 'Nu',
        customClass: {
          popup: 'popup-z-elevate-24-blue'
        }
      }).then((result) => {
        this.operatorService.createElectricityNetworkContract(this.newNetworkContract).subscribe((val) => {
          this.successCreateNotification();
          this.editable = false;
        }, error => {
          if (error) {
            this.errorNotification(error.error.message);
          }
        });
      })
    }
  }

  createConvention(): void {
    if (this.conventionForm.invalid) {
      this.validationNotification('Vă rugăm să completați toate câmpurile marcate cu asterix')
    } else {
      let placeConvention = new PlaceConvention();
      placeConvention.conventionNumber = this.conventionForm.get('conventionNumber').value;
      placeConvention.conventionDate = this.conventionForm.get('conventionDate').value;
      placeConvention.placeId = this.conventionFilter.placeId;

      Swal.fire({
        title: 'Esti sigur?',
        text: this.conventionConfirmMessage(),
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Da',
        cancelButtonText: 'Nu',
        customClass: {
          popup: 'popup-z-elevate-24-blue'
        }
      }).then((result) => {
        if (result.isConfirmed) {
          this.operatorService.createConventionForNetworkContract(placeConvention).subscribe(() => {
            if (!this.isConventionUpdated) {
              this.successCreateConventionNotification();
            } else {
              this.successUpdateConventionNotification();
            }
            this.modalReference.close();
            this.searchPlaces();
          }, error => {
            if (error) {
              this.errorNotification(error.error.message);
            }
          });
        }
      })
    }
  }

  conventionConfirmMessage(): string {
    if (!this.isConventionUpdated) {
      return 'Esti pe cale sa adaugi o conventie noua.';
    } else {
      return 'Esti pe cale sa modifici o conventie.';
    }
  }

  isClient(): boolean {
    if (this.form.get('client').value === 'CLIENT')
      return true;
    else
      return false;
  }

  isSupplier(): boolean {
    if (this.form.get('client').value === 'SUPPLIER') {
      this.isSupplierSelected = true;
      this.checkConsumptionPlaceVisibility();
      return true;
    } else {
      return false;
    }
  }

  isIndividual(): boolean {
    if (this.form.get('clientType').value === 'INDIVIDUAL')
      return true;
    else
      return false;
  }

  isLegalPerson(): boolean {
    if (this.form.get('clientType').value === 'LEGAL_PERSON')
      return true;
    else
      return false;
  }

  setClient(value: string): void {
    this.form.controls['client'].setValue(value);
    if (value === 'SUPPLIER') {
      this.isSupplierSelected = true;
    }
    if (value === 'CLIENT') {
      this.isSupplierSelected = false;
    }
    this.checkConsumptionPlaceVisibility();
    this.form.controls['clientType'].setValue(null);
  }

  setClientType(value: any): void {
    this.form.controls['clientType'].setValue(value);
  }

  checkConsumptionPlaceVisibility(): void {
    if (this.form.get('clientCode').value.length > 0 && this.isSupplierSelected == false || this.isSupplierSelected == true) {
      this.consumptionPlacesListVisible = true;
    } else {
      this.consumptionPlacesListVisible = false;
    }
  }

  searchPlaces(): void {
    this.placeFilterForm.networkContractId = this.networkContractId;
    this.placeFilterForm.clientCode = this.form.get('clientCode').value;
    this.placeFilterForm.client = this.form.get('client').value;
    this.placeFilterForm.clientType = this.form.get('clientType').value;
    this.placeFilterForm.placeType = 'POD';

    this.operatorService.getPlacesFromContracts(this.placeFilterForm).subscribe((val) => {
      this.electricityConsumptionPlaces = val;
      this.placeFilterForm.pageOffset = val.number;
      this.paginationElectricityConsumptionPlaces = [{ pageNumber: val.number, className: 'page-item active' }];
    }, error => {
      if (error) {
        this.errorNotification(error.error.message);
      }
    });
  }

  validationNotification(message: string): void {
    Swal.fire({
      title: ' ',
      text: message,
      customClass: {
        popup: 'popup-z-elevate-24-blue'
      }
    });
  }

  placesNotFoundNotification(message: string): void {
    Swal.fire({
      title: ' ',
      text: message,
      customClass: {
        popup: 'popup-z-elevate-24-blue'
      }
    });
  }

  errorNotification(message: string): void {
    Swal.fire({
      title: ' ',
      text: message,
      customClass: {
        popup: 'popup-z-elevate-24-blue'
      }
    });
  }

  successCreateNotification(): void {
    Swal.fire({
      title: ' ',
      text: 'Contractul de retea a fost creat cu succes',
      customClass: {
        popup: 'popup-z-elevate-24-blue'
      }
    });
  }

  successCreateConventionNotification(): void {
    Swal.fire({
      title: ' ',
      text: 'Conventia a fost creata cu succes',
      customClass: {
        popup: 'popup-z-elevate-24-blue'
      }
    });
  }

  successUpdateConventionNotification(): void {
    Swal.fire({
      title: ' ',
      text: 'Conventia a fost modificata cu succes',
      customClass: {
        popup: 'popup-z-elevate-24-blue'
      }
    });
  }


  ngOnDestroy(): void {
    this.routeDataSub.unsubscribe();
    if (this.formSubscriptions) {
      for (const subscription of this.formSubscriptions) {
        if (subscription) {
          subscription.unsubscribe();
        }
      }
    }
  }

  openConvention(content: any, place: any): void {
    this.conventionFilter.placeId = place.id;
    this.conventionFilter.clientCode = this.form.get('clientCode').value;
    this.conventionFilter.client = this.form.get('client').value;
    this.conventionFilter.clientType = this.form.get('clientType').value;
    this.operatorService.getConventionParts(this.conventionFilter).subscribe((val) => {
      this.conventionParts = val;
    });
    if (place.conventionNumber != null || place.conventionDate) {
      this.isConventionUpdated = true;
    }
    this.conventionForm.get('conventionNumber').setValue(place.conventionNumber);
    this.conventionForm.get('conventionDate').setValue(place.conventionDate);
    this.modalReference = this.modalService.open(content);
    this.modalReference.result.then((result) => {
      this.closeResult = `Closed with: ${result}`;
    }, (reason) => {
      this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
    });
  }

  private getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) {
      return 'by pressing ESC';
    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
      return 'by clicking on a backdrop';
    } else {
      return `with: ${reason}`;
    }
  }

  onChangeCounty(): void {
    this.cities = [];
    this.addressService.getCities(this.placeForm.get('county').value)
      .subscribe((cities: CityDTO[]) => this.cities = cities);
  }

  public consumptionPlaceSettings = {
    actions: {
      custom: [
        {
          name: 'view',
          title: '<p>Detalii</p>'
        },
        /*{
          name: 'edit',
          title: '<p>Editeaza</p>'
        },
        {
          name: 'inactivate',
          title: '<p class="action-hide">Inactiveaza</p>'
        },*/
      ],
      add: false,
      edit: false,
      delete: false,
      position: 'right',
      columnTitle: 'Opțiuni',
    },
    rowClassFunction: (row: any) => {
      if (row.data.status === 'INACTIV') {
        return 'row_INACTIVATE_HIDE';
      }
    },
    hideSubHeader: true,
    columns: {
      code: {
        title: 'POD'
      },
      address: {
        title: 'Adresa'
      },
      status: {
        title: 'Status'
      }
    },
  };

  onCustomActionConsumptionPlace(event: any): void {
    switch (event.action) {
      case 'view':
        this.router.navigate(['../detalii-loc-consum'], {
          relativeTo: this.route,
          state: { placeId: event.data.id, editable: false }
        });
        break;
      case 'edit':
        this.router.navigate(['../editare-loc-consum'], {
          relativeTo: this.route,
          state: { placeId: event.data.id, editable: true }
        });
        break;
      case 'inactivate':
        this.operatorService.inactivateStatusConsumptionPlace(event.data.code).subscribe(() => {
          this.searchPlaces();
          this.toastrService.success('Loc de consum inactivat.');
        }
        );
        break;
    }
  }

  changePage(page: any): void {
    this.placeFilterForm.pageOffset = page.pageNumber;
    this.searchPlaces();
  }

  previousPage(): void {
    this.placeFilterForm.pageOffset = this.placeFilterForm.pageOffset === 0 ? 0 : this.placeFilterForm.pageOffset - 1;
    this.searchPlaces();
  }

  nextPage(): void {
    this.placeFilterForm.pageOffset = this.placeFilterForm.pageOffset === this.electricityConsumptionPlaces.totalPages - 1
      ? this.placeFilterForm.pageOffset : this.placeFilterForm.pageOffset + 1;
    this.searchPlaces();
  }


}
